import { useEffect } from 'react';
import { useQuery } from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { usePublicLinkPageDataProvider } from '@confluence/public-links';
import { usePageSpaceKey } from '@confluence/page-context';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';
import { useSessionData } from '@confluence/session-data';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../controller/Controller';
import type { ContentType } from '../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator';
import type { QueriesState } from '../Controller';

import { dataToQueriesState } from './dataToQueriesState';
import {
	type ShareAndRestrictDialogQuery as ShareAndRestrictDialogQueryType,
	type ShareAndRestrictDialogQueryVariables,
} from './__types__/ShareAndRestrictDialogQuery';
import { ShareAndRestrictDialogQuery } from './ShareAndRestrictDialogQuery.graphql';
import {
	type PublishModalRestrictionsInput,
	publishModalDataToQueriesState,
} from './publishModalDataToQueriesState';
import { usePreloadUserSuggestions } from './useUserSuggestions';

export type QueriesHookResponse =
	| { state: 'loading' }
	| { state: 'error' }
	| { state: 'loaded'; queriesState: QueriesState };

const useDraftShareId = createSingleQueryParamHook('draftShareId');

export const useQueries = ({
	contentId,
	contentType,
	publishModalRestrictions,
	spaceKey: inputSpaceKey,
}: {
	contentId: string;
	contentType: ContentType;
	publishModalRestrictions?: PublishModalRestrictionsInput;
	spaceKey?: string;
}): QueriesHookResponse => {
	const [defaultSpaceKey] = usePageSpaceKey();
	const spaceKey = inputSpaceKey || defaultSpaceKey;
	const draftShareId = useDraftShareId() ?? '';
	const { edition } = useSessionData();
	const { loading, error, data } = useQuery<
		ShareAndRestrictDialogQueryType,
		ShareAndRestrictDialogQueryVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		ShareAndRestrictDialogQuery,
		{
			variables: {
				contentId,
				// In order to allow us to fetch any data for draft content always we need to specify the status
				status: ['current', 'draft', 'archived'],
				draftShareId,
				spaceKey: spaceKey || '',
			},
			fetchPolicy: 'network-only',
		},
	);

	const publicLinkPageData = usePublicLinkPageDataProvider({
		pageId: contentId,
		fetchPolicy: 'network-only',
	});

	usePreloadUserSuggestions();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'shareAndRestrictDialogQuery',
					attributes: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
						cause: error?.cause,
					},
				},
			}).fire();
		}
	}, [error, createAnalyticsEvent]);

	if (loading) return { state: 'loading' };

	if (error) {
		// We never expect any errors here. Don't mark it as handled.
		return { state: 'error' };
	}

	// Translate the query to our internal state. If that was unsuccessful
	// (most likely because of insufficient data), consider it an error
	if (!data) return { state: 'error' };

	const queriesState = (() => {
		if (publishModalRestrictions) {
			return data && edition
				? publishModalDataToQueriesState({
						publishModalRestrictions,
						spaceKey: spaceKey || '',
						data,
						publicLinkPageData,
						contentType,
						edition,
					})
				: undefined;
		}
		return data && edition
			? dataToQueriesState(data, publicLinkPageData, spaceKey || '', contentType, edition)
			: undefined;
	})();

	if (!queriesState) return { state: 'error' };

	return { state: 'loaded', queriesState };
};
